import React, { useState } from "react";
import { Download, Mail, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { downloadWaitlistCsv } from "@/lib/api";

/**
 * WaitlistExport — admin button to download the waitlist as CSV.
 * Two options: All entries vs. Newsletter opt-ins only.
 */
export default function WaitlistExport() {
  const [pending, setPending] = useState(null); // 'all' | 'newsletter' | null
  const [lastExport, setLastExport] = useState(null);

  const handleExport = async (newsletterOnly) => {
    const kind = newsletterOnly ? "newsletter" : "all";
    try {
      setPending(kind);
      const { filename, totalRows } = await downloadWaitlistCsv({ newsletterOnly });
      setLastExport({
        kind,
        filename,
        rows: totalRows,
        at: new Date(),
      });
      toast.success(
        `${totalRows} ${totalRows === 1 ? "Eintrag" : "Einträge"} exportiert: ${filename}`,
      );
    } catch (err) {
      toast.error(`Export fehlgeschlagen: ${err?.message || "Unknown error"}`);
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="card-standard" data-testid="waitlist-export" style={{ padding: 24 }}>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="form-label" style={{ color: "var(--color-accent)" }}>
            <FileSpreadsheet size={12} className="inline -mt-0.5 mr-1" />
            Waitlist-Export
          </div>
          <div className="body-md mt-1">
            Exportiere alle Waitlist-Einträge als CSV — optional gefiltert auf Newsletter-Opt-ins.
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <ExportCard
          title="Komplette Waitlist"
          description="Alle Einträge inkl. derer ohne Newsletter-Zustimmung. Für interne Auswertung & Backup."
          pending={pending === "all"}
          onClick={() => handleExport(false)}
          testId="export-all"
        />
        <ExportCard
          title="Newsletter-Segment"
          description="Nur Einträge mit aktivem Newsletter-Opt-in. Direkt importierbar in dein E-Mail-Tool (Mailjet, Brevo, Mailchimp …)."
          pending={pending === "newsletter"}
          onClick={() => handleExport(true)}
          accent
          testId="export-newsletter"
        />
      </div>

      {lastExport && (
        <div
          className="mt-4 text-[11px] text-muted flex items-center gap-2"
          data-testid="last-export"
        >
          <Mail size={11} />
          Zuletzt exportiert: <strong className="text-primary font-medium">{lastExport.filename}</strong>
          <span>·</span>
          <span>{lastExport.rows} Zeilen</span>
        </div>
      )}

      <div className="mt-4 text-[11px] text-muted leading-relaxed">
        CSV mit UTF-8-BOM (Excel-kompatibel), Spalten: <code className="text-primary">created_at, email, phone, newsletter_opt_in, source, restaurant_id, shop_id, qr_source, utm_source, utm_medium, utm_campaign</code>.
      </div>
    </div>
  );
}

function ExportCard({ title, description, pending, onClick, accent, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      data-testid={testId}
      className={`text-left border rounded-sm p-4 transition-colors group ${
        accent
          ? "border-soft hover:border-accent bg-surface/40"
          : "border-soft hover:border-primary"
      } ${pending ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
    >
      <div className="flex items-start justify-between mb-2">
        <span className="text-[14px] font-medium text-primary">{title}</span>
        <Download
          size={14}
          className={`shrink-0 mt-0.5 transition-colors ${accent ? "text-accent" : "text-muted group-hover:text-primary"}`}
        />
      </div>
      <p className="text-[12px] text-muted leading-snug">{description}</p>
      {pending && (
        <span className="text-[11px] text-accent block mt-2">Wird vorbereitet…</span>
      )}
    </button>
  );
}
